import { Fragment, useCallback, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { debounce } from 'throttle-debounce';
// Store
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Async
import { getAuthor, getAuthors } from 'app/store/EpisodeAuthors/EpisodeAuthors.async';
// Actions
import { EpisodeAuthorsActions } from 'app/store/EpisodeAuthors/EpisodeAuthors.slice';
// Selectors
import { selectAuthorsAsOptions } from 'app/store/EpisodeAuthors/EpisodeAuthors.selectors';
// Mui
import {
  type AutocompleteChangeReason,
  type AutocompleteInputChangeReason,
  ClickAwayListener,
  Autocomplete as MuiAutocomplete,
  TextField as MuiTextField,
} from '@mui/material';
// Icons
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
// Components
// import { Autocomplete } from 'app/components/Mui/Autocompletes';
import AuthorTPMPopover from './components/AuthorTPMPopover';
// Utils
import { trimValue } from 'app/utilities/Utilities';
import useAuthorTypeGroupContext from './utils/useAuthorTypeGroupContext';

const AuthorControl = () => {
  const { t } = useTranslation();
  
  const { control, setValue, getValues } = useFormContext();
  const { openAuthorAutocomplete, onOpenAuthorAutocomplete, onCloseAuthorAutocomplete, ControlProps, setTypes } = useAuthorTypeGroupContext();

  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const authorOptions = useAppSelector(selectAuthorsAsOptions);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceGetAuthors = useCallback(debounce(500, (newValue:string) => {
    dispatch(getAuthors({ namePrefix: newValue }));
  }), []);

  const handleChange = (_:any, newValue:any, reason:AutocompleteChangeReason) => {
    if ( reason === 'clear' ) {
      setValue('author', {});
      dispatch(EpisodeAuthorsActions.setAuthor(null));
      return;
    }

    if ( !newValue || !authorOptions ){
      setValue('author', {});
      setValue('type', null);
      setTypes([]);
      return;
    }

    const findOption = authorOptions.find((option:any) => option.id === newValue.id);

    if ( !findOption ) return;

    const { id, name, props } = findOption;

    setValue('author', { id, name });

    if ( props.episodeTypes && props.episodeTypes.length ){
      setValue('type', props.episodeTypes[0]);
      setTypes(props.episodeTypes);
    }

    if ( id ){
      // Get author third party matches
      dispatch(getAuthor({
        authorId: id,
        params: { fields: 'thirdPartyMatches' }
      }));
    }
  };

  const handleInputChange = (_:any, newValue:string, reason:AutocompleteInputChangeReason) => {
    if ( reason !== 'input' ) return;

    const trimedValue = trimValue(newValue);

    setValue('author', { name: newValue });

    debounceGetAuthors(trimedValue);
  }

  useEffect(() => {
    const author = getValues('author');

    const asyncGetAuthor = async () => {
      const response = await dispatch(getAuthor({
        authorId: author.id,
        params: { fields: 'thirdPartyMatches' }
      })).unwrap();
      // Set authorOptions to keep compatibility with Autocomplete: name = "author"
      dispatch(EpisodeAuthorsActions.setAuthors([response]));
    }

    if ( author ) asyncGetAuthor();
    // eslint-disable-next-line
  }, []);

  return (
    <Controller
      control={control} name="author" defaultValue={null}
      render={({ field }) => (
        <ClickAwayListener onClickAway={onCloseAuthorAutocomplete}>
          <MuiAutocomplete
            {...field}
            open={openAuthorAutocomplete}
            onOpen={onOpenAuthorAutocomplete}
            onClose={onCloseAuthorAutocomplete}
            disabled={ControlProps?.disabled}
            options={authorOptions || []}
            onChange={handleChange}
            onInputChange={handleInputChange}
            renderInput={(params) => (
              <MuiTextField
                {...params}
                label={t('labels.author')} name="author"
                sx={{
                  '#thirdPartyMatchesButton': {
                    opacity: 0,
                    visibility: 'hidden'
                  },
                  '&:hover, .Mui-focused': {
                    '#thirdPartyMatchesButton': {
                      opacity: 1,
                      visibility: 'visible'
                    }
                  }
                }}
                size="small"
                margin="none"
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <Fragment>
                        <AuthorTPMPopover />
                        {params.InputProps.endAdornment}
                      </Fragment>
                    )
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.props.verified ? (
                  <CheckCircleIcon sx={{ mr: 2 }} color="success" />
                ) : null}
                {option.name}
              </li>
            )}
            freeSolo
            fullWidth
            getOptionLabel={(option) => {
              if ( typeof option !== 'string' ) return option.name || '';
              return option;
            }}
          />
        </ClickAwayListener>
      )}
    />
  )
}

export default AuthorControl;
